/* eslint-disable id-length */
/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { Popover } from '@mui/material';
import dayjs from 'dayjs';
import './dateRangePicker.scss';
import ButtonComponent from '../Button/button';
import { DATE_RANGE_PICKER } from '../../Constants/strings';
import CustomCalendarHeader from './customCalenderHeader';

const CustomDateRangePicker = ({
  anchorEl,
  onClose,
  onChange,
  startDate,
  endDate,
  dateCancelCss,
  dateApplyCss,
  dateRangePickerCss,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
}) => {
  const open = Boolean(anchorEl);
  const currentDate = dayjs(); // Use dayjs for consistent date handling

  // Use default values if dates are not selected
  const defaultStartDate = startDate;

  // Handle date range change (start and end)
  const handleDateChange = (newDate) => {
    if (startDate && (newDate.isSame(startDate, 'day') || newDate.isAfter(startDate))) {
      onChange([startDate, newDate]);
    } else {
      onChange([newDate, null]);
    }
  };

  const handleReset = () => {
    onChange([null, null]); // Reset both dates
  };

  const handleApply = () => {
    onClose(); // Close the date picker
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin} // Use the anchorOrigin prop
      >
        <div className={`date-range-picker-content ${dateRangePickerCss}`}>
          <div className="date-range-picker-column">
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={defaultStartDate}
              onChange={handleDateChange}
              maxDate={currentDate}
              toolbar={false}
              slots={{ calendarHeader: CustomCalendarHeader }}
              slotProps={{
                calendarHeader: { selectedStartDate: startDate, selectedEndDate: endDate },
                day: (day) => {
                  const dayDate = dayjs(day.day); // Wrap the day to ensure a valid dayjs instance

                  if (!dayDate.isValid()) {
                    return { className: 'custom-day' }; // Fallback class if invalid
                  }

                  return {
                    className: [
                      'custom-day',
                      startDate && dayDate.isSame(startDate, day.day) && 'start-date',
                      endDate && dayDate.isSame(endDate, day.day) && 'end-date',
                      startDate && endDate && dayDate.isAfter(startDate, day.day) && dayDate.isBefore(endDate, day.day) && 'in-range',
                    ]
                      .filter(Boolean)
                      .join(' '),
                  };
                },
              }}

            />
          </div>
        </div>
        <div className="date-range-picker-footer">
          <ButtonComponent
            label={DATE_RANGE_PICKER.RESET}
            variant="text"
            onClick={handleReset}
            btnCss={`cancel-btn ${dateCancelCss}`}
            id="date-reset"
          ></ButtonComponent>
          <ButtonComponent
            label={DATE_RANGE_PICKER.APPLY}
            variant="contained"
            onClick={handleApply}
            btnCss={`apply-btn ${dateApplyCss}`}
            id="date-apply"
          ></ButtonComponent>
        </div>
      </Popover>
    </LocalizationProvider>
  );
};

CustomDateRangePicker.propTypes = {
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  startDate: PropTypes.instanceOf(dayjs),
  endDate: PropTypes.instanceOf(dayjs),
  dateCancelCss: PropTypes.string,
  dateApplyCss: PropTypes.string,
  dateRangePickerCss: PropTypes.string,
  anchorOrigin: PropTypes.object,
};

export default CustomDateRangePicker;
